import { CheckCircleOutlined, CheckOutlined, PlusOutlined } from '@ant-design/icons'
import { useDebounce } from 'ahooks'
import { AutoComplete, Button, Modal, Tag, message } from 'antd'
import { getOperationName } from 'apollo-utilities'
import produce from 'immer'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  GetUserSkillDocument,
  useGetFrequentSkillsQuery,
  useGetSkillSearchQuery,
  useSkillCreateMutation,
  useUserSkillBatchUpdateMutation,
} from '@/generated/graphql'
import { ga, traceEvent } from '@/utils/ga'

import s from './user-info-skill-modal.module.less'

export const UserInfoSkillModal: React.FC<{
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
  userId: number
  userSkills: { name: string; skillId: number }[]
  readonly?: boolean
}> = ({ visible, setVisible, userId, readonly, userSkills }) => {
  const { t } = useTranslation()
  const [skills, setSkills] = React.useState<{ skillId: number; name: string; isSelected: boolean }[]>([])

  const [userSkillUpdate] = useUserSkillBatchUpdateMutation({
    refetchQueries: [getOperationName(GetUserSkillDocument)!],
    onCompleted() {
      const selectedSkills = skills.filter((skill) => skill.isSelected)
      if (selectedSkills.length > 0) {
        const category = traceEvent.profile
        ga('event', category.events.click_add_skills, {
          event_category: category.event_category,
          event_label: readonly ? '0' : '1',
        })
      }
      setVisible(false)
    },
    onError() {
      setVisible(false)
    },
  })

  const onOK = () => {
    if (skills.filter((skill) => skill.isSelected).length > 0) {
      userSkillUpdate({
        variables: {
          userId,
          skillIds: skills.filter((skill) => skill.isSelected).map((skill) => skill.skillId),
        },
      })
    }
  }

  const footer = (
    <div className={s.footer}>
      <Button type='text' onClick={() => setVisible(false)}>
        {t('user.info.cancel')}
      </Button>
      <Button type='primary' onClick={() => onOK()}>
        {t('user.info.save')}
      </Button>
    </div>
  )

  return (
    <Modal
      title={t(`user.info.skill.${readonly ? 'admitTag' : 'addTag'}`)}
      visible={visible}
      footer={footer}
      closable={false}
      maskClosable={false}
      destroyOnClose
    >
      <UserInfoSkillModalInner userId={userId} userSkills={userSkills} skills={skills} setSkills={setSkills} />
    </Modal>
  )
}

export const UserInfoSkillModalInner: React.FC<{
  userId: number
  userSkills: { name: string; skillId: number }[]
  skills: {
    skillId: number
    name: string
    isSelected: boolean
  }[]
  setSkills: React.Dispatch<
    React.SetStateAction<
      {
        skillId: number
        name: string
        isSelected: boolean
      }[]
    >
  >
}> = ({ userId, userSkills, skills, setSkills }) => {
  const { t } = useTranslation()
  const [showSearch, setShowSearch] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebounce(search, { wait: 500 })

  const { data: frequentSkillData } = useGetFrequentSkillsQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      input: {
        top: 10,
      },
      userId,
    },
  })

  const { data } = useGetSkillSearchQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      input: {
        search: debouncedSearch,
      },
      userId,
    },
  })

  const [skillCreate] = useSkillCreateMutation({
    onCompleted(data) {
      const skill = data.skillCreate
      if (skill) {
        setSkills((skills) => [...skills.slice(), { ...skill, isSelected: true }])
        setSearch('')
        message.info('添加技能成功')
      } else {
        message.warn('添加技能失败，可能原因：已存在')
      }
    },
  })

  React.useEffect(() => {
    if (frequentSkillData) {
      setSkills(frequentSkillData.skills.map((skill) => ({ ...skill, isSelected: false })))
    }
  }, [frequentSkillData])

  const skillCandidates = data?.skills.slice(0, 6) || []

  const options =
    skillCandidates.length > 0
      ? skillCandidates.map((value) => ({
          label: (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <span>{value.name}</span>
              {userSkills.some((userSkill) => userSkill.skillId === value.skillId) && (
                <Tag style={{ border: 0, marginLeft: 8 }} icon={<CheckCircleOutlined />} color='success'>
                  {t('user.info.skill.alreadyAdded')}
                </Tag>
              )}
            </div>
          ),
          value: value.skillId,
        }))
      : [{ label: <span>{search}</span>, value: 0 }]

  const searchComponent = (
    <div className={s.searchWrapper}>
      {showSearch ? (
        <AutoComplete
          defaultActiveFirstOption={true}
          filterOption={false}
          value={search}
          placeholder={t('user.info.skill.searchPlaceholder')}
          className={s.search}
          onSelect={(id: any) => {
            if (Number(id) === 0 && search !== '') {
              skillCreate({
                variables: {
                  name: search,
                },
              })
              return
            }

            // 选择的技能已存在
            if (userSkills.some((userSkill) => userSkill.skillId === Number(id))) {
              return
            }
            const candidate = skillCandidates.find((candidate) => candidate.skillId === Number(id))
            if (candidate) {
              setSearch(candidate.name)
              // 选择的技能已在添加列表
              let newState = skills.slice()
              if (skills.some((skill) => skill.skillId === Number(id))) {
                newState = produce(skills, (draft) => {
                  const skill = draft.find((skill) => skill.skillId === Number(id))
                  skill!.isSelected = !skill!.isSelected
                })
              } else {
                newState = produce(skills, (draft) => {
                  draft.push({
                    ...skillCandidates.find((candidate) => candidate.skillId === Number(id))!,
                    isSelected: true,
                  })
                })
              }
              setSkills(newState)
            }
          }}
          options={options}
          onSearch={(value) => {
            setSearch(value)
          }}
        />
      ) : (
        <Button type='text' icon={<PlusOutlined />} size='large' onClick={() => setShowSearch(true)}>
          {t('user.info.skill.addTag')}
        </Button>
      )}
    </div>
  )

  const frequentTags = skills.length > 0 && (
    <div className={s.frequentWrapper}>
      <div className={s.title}>
        <span style={{ color: '#65676b' }}>{t('user.info.skill.frequent')}</span>
      </div>
      <div className={s.skills}>
        {skills.map((skill) => {
          const isSelected = skills.some((s) => s.skillId === skill.skillId && s.isSelected)
          const Icon = isSelected ? CheckOutlined : PlusOutlined
          return (
            <Tag
              color={isSelected ? '#48bbb5' : undefined}
              style={{ backgroundColor: isSelected ? '#48bbb5' : undefined }}
              onClick={() => {
                const state = produce(skills, (draft) => {
                  const selectedSkill = draft.find((s) => s.skillId === skill.skillId)
                  selectedSkill!.isSelected = !selectedSkill!.isSelected
                })
                setSkills(state)
              }}
              key={skill.skillId}
            >
              <div style={{ padding: 4 }}>
                {skill.name}
                <Icon style={{ marginLeft: 8 }} />
              </div>
            </Tag>
          )
        })}
      </div>
    </div>
  )

  return (
    <div className={s.modal}>
      {frequentTags}
      {searchComponent}
    </div>
  )
}
